import { useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import MarkEmailReadRounded from '@mui/icons-material/MarkEmailReadRounded';
import VerifiedUserRounded from '@mui/icons-material/VerifiedUserRounded';
import { ApiError } from '@/api';
import { useAuth } from '@/auth';
import { FormField } from '@/components/form/FormField';
import { useEu } from '@/features/notas/EuContext';
import { cancelaPendente, pedeVinculo } from '@/features/notas/service';

type Campo = 'ra' | 'nome' | 'email';

export function VincularPage() {
  const { user } = useAuth();
  const { eu, recarrega } = useEu();
  const [ra, setRa] = useState('');
  const [nome, setNome] = useState(user?.name?.toUpperCase() ?? '');
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<{ campo?: Campo; msg: string } | null>(null);
  const [enviadoPara, setEnviadoPara] = useState<string | null>(eu.pendente?.email ?? null);

  const envia = async (e: FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const r = await pedeVinculo({ ra: ra.trim(), nome: nome.trim(), email: email.trim() });
      setEnviadoPara(r.email);
    } catch (err) {
      const d =
        err instanceof ApiError
          ? (err.data as { campo?: Campo; message?: string } | undefined)
          : undefined;
      setErro({
        campo: d?.campo,
        msg: d?.message ?? 'Não deu certo agora. Tente de novo em instantes.',
      });
    } finally {
      setEnviando(false);
    }
  };

  const corrigir = async () => {
    await cancelaPendente().catch(() => {});
    setEnviadoPara(null);
    recarrega();
  };

  return (
    <Box sx={{ maxWidth: 620, mx: 'auto' }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1">
          Antes de ver as suas notas
        </Typography>
        <Typography color="text.secondary">
          Confirme quem você é. Assim ninguém além de você vê o seu desempenho.
        </Typography>
      </Stack>

      <Stepper activeStep={enviadoPara ? 1 : 0} alternativeLabel sx={{ mb: 3 }}>
        <Step>
          <StepLabel>Seus dados do SIGA</StepLabel>
        </Step>
        <Step>
          <StepLabel>Link no e-mail @cps</StepLabel>
        </Step>
        <Step>
          <StepLabel>Painel liberado</StepLabel>
        </Step>
      </Stepper>

      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          {enviadoPara ? (
            <Stack spacing={2.5} sx={{ alignItems: 'center', textAlign: 'center', py: 2 }}>
              <MarkEmailReadRounded color="primary" sx={{ fontSize: 56 }} />
              <Typography variant="h5">Confira o seu e-mail institucional</Typography>
              <Typography color="text.secondary">
                Mandei um link para <strong>{enviadoPara}</strong>. Abra o e-mail (no Outlook ou no
                Teams) e clique em <strong>"Sou eu, confirmar"</strong>. O link vale por 24 horas;
                pode abrir no celular.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Não chegou? Olhe a pasta de lixo eletrônico. Se precisar, corrija os dados e peça de
                novo.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" onClick={() => window.location.reload()}>
                  Já confirmei
                </Button>
                <Button onClick={() => void corrigir()}>Corrigir os dados</Button>
              </Stack>
            </Stack>
          ) : (
            <Box component="form" noValidate onSubmit={(e) => void envia(e)}>
              <Stack spacing={2.5}>
                {erro && !erro.campo && <Alert severity="error">{erro.msg}</Alert>}
                <FormField
                  label="RA"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Ex.: 2040482512345"
                  value={ra}
                  onChange={(e) => setRa(e.target.value.replace(/[^\d]/g, ''))}
                  error={erro?.campo === 'ra'}
                  helperText={erro?.campo === 'ra' ? erro.msg : 'Só números, como aparece no SIGA.'}
                  required
                />
                <FormField
                  label="Nome completo"
                  autoComplete="name"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  error={erro?.campo === 'nome'}
                  helperText={
                    erro?.campo === 'nome'
                      ? erro.msg
                      : 'Sem abreviações, igual ao SIGA. Acentos e maiúsculas não importam.'
                  }
                  required
                />
                <FormField
                  label="E-mail institucional"
                  type="email"
                  autoComplete="email"
                  placeholder="nome.sobrenome@aluno.cps.sp.gov.br"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={erro?.campo === 'email'}
                  helperText={
                    erro?.campo === 'email'
                      ? erro.msg
                      : 'O @aluno.cps.sp.gov.br que você usa no Teams/Outlook.'
                  }
                  required
                />
                <Alert icon={<VerifiedUserRounded />} severity="info" variant="outlined">
                  Você está entrando como <strong>{user?.email}</strong>. Depois de confirmado, o RA
                  fica ligado a esta conta Google.
                </Alert>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={enviando || !ra || !nome || !email}
                  startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                  Enviar link de confirmação
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
